<template>
	<div class="relative min-w-0" :data-testid="testid">
		<!-- Mouse users can also drag the row; touch keeps native scrolling. -->
		<div
			ref="stripRef"
			class="no-scrollbar max-w-full select-none overflow-x-auto overscroll-x-contain touch-pan-x"
			:class="[TAB_GROUP_PANEL_CLASS, dragging ? 'cursor-grabbing' : (fadeLeft || fadeRight ? 'cursor-grab' : '')]"
			role="tablist"
			:aria-label="ariaLabel"
			@scroll.passive="updateFades"
			@keydown="onTablistKeydown"
			@pointerdown="onPointerDown"
			@pointermove="onPointerMove"
			@pointerup="onPointerUp"
			@pointercancel="onPointerUp"
		>
			<button
				v-for="t in tabs"
				:key="t.key"
				:ref="(el) => setTabRef(t.key, el)"
				type="button"
				role="tab"
				:class="tabItemClass(active === t.key)"
				:aria-selected="active === t.key"
				:tabindex="active === t.key ? 0 : -1"
				@click="onTabClick(t.key)"
			>
				{{ t.label }}
			</button>
		</div>
		<!-- Fade edges + chevrons only while there is more strip in that direction.
			Keyboard users have arrow keys, so the chevrons stay out of the a11y tree. -->
		<div v-show="fadeLeft" class="pointer-events-none absolute inset-y-0 left-0 w-12 rounded-l-lg bg-gradient-to-r from-surface-2 via-surface-2/80 to-transparent" aria-hidden="true" />
		<div v-show="fadeRight" class="pointer-events-none absolute inset-y-0 right-0 w-14 rounded-r-lg bg-gradient-to-l from-surface-2 via-surface-2/85 to-transparent" aria-hidden="true" />
		<button
			v-show="fadeLeft"
			type="button"
			class="absolute left-0.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-surface-8 hover:bg-surface-3 hover:text-white"
			aria-hidden="true"
			tabindex="-1"
			:data-testid="`${testid}-prev`"
			@click="scrollStrip(-1)"
		>
			<icon-base :width="12" :height="12" width-view-box="16" height-view-box="16"><icon-chevron-left /></icon-base>
		</button>
		<button
			v-show="fadeRight"
			type="button"
			class="absolute right-0.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-surface-8 hover:bg-surface-3 hover:text-white"
			aria-hidden="true"
			tabindex="-1"
			:data-testid="`${testid}-next`"
			@click="scrollStrip(1)"
		>
			<icon-base :width="12" :height="12" width-view-box="16" height-view-box="16"><icon-chevron-right /></icon-base>
		</button>
	</div>
</template>

<script setup>
// Single-row segmented tablist that scrolls sideways when it does not fit:
// edge fades, ghost chevrons, mouse drag-to-scroll, arrow-key navigation.
// Shared by the Trend metric tabs and the Audience breakdown tabs.
import { ref } from 'vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconChevronLeft from '@/components/icon/IconChevronLeft.vue';
import IconChevronRight from '@/components/icon/IconChevronRight.vue';
import { TAB_GROUP_PANEL_CLASS, tabItemClass } from './analyticsTabs';
import { useTabScroll } from './useTabScroll';

const props = defineProps({
	// [{ key, label }]
	tabs: { type: Array, required: true },
	active: { type: String, required: true },
	ariaLabel: { type: String, default: 'Tabs' },
	testid: { type: String, default: 'analytics-tabs' },
});

const emit = defineEmits(['update:active']);

const stripRef = ref(null);
const {
	fadeLeft, fadeRight, dragging, setTabRef, updateFades, scrollStrip,
	onPointerDown, onPointerMove, onPointerUp, onTabClick, onTablistKeydown,
} = useTabScroll({
	stripRef,
	keys: () => props.tabs.map((t) => t.key),
	active: () => props.active,
	select: (key) => emit('update:active', key),
});
</script>

<style scoped>
.no-scrollbar {
	scrollbar-width: none;
}
.no-scrollbar::-webkit-scrollbar {
	display: none;
}
</style>
